import React, { useState, useEffect } from 'react';
import './AdminTestRunner.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AdminTestRunner() {
  const [testRuns, setTestRuns] = useState([]);
  const [currentResults, setCurrentResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvValidation, setCsvValidation] = useState(null);
  const [testName, setTestName] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  useEffect(() => {
    loadTestHistory();
  }, []);

  const loadTestHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/test-runs/history`);
      const data = await response.json();
      setTestRuns(data);
    } catch (error) {
      console.error('Failed to load test history:', error);
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/v1/admin/test-runs/csv`, {
        method: 'POST',
        body: formData
      });

      const validation = await response.json();
      setCsvValidation(validation);
    } catch (error) {
      console.error('CSV validation failed:', error);
      setCsvValidation({
        valid: false,
        message: 'Upload failed',
        errors: [error.message]
      });
    } finally {
      setLoading(false);
    }
  };

  const executeTestRun = async () => {
    if (!csvFile || !csvValidation?.valid) return;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await fetch(
        `${API_URL}/api/v1/admin/test-runs/csv/execute?name=${encodeURIComponent(testName || csvFile.name)}`,
        {
          method: 'POST',
          body: formData
        }
      );

      const results = await response.json();
      setCurrentResults(results);
      loadTestHistory();
    } catch (error) {
      console.error('Test run failed:', error);
      alert('Test run failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      'LOW': '#4caf50',
      'MEDIUM': '#ff9800',
      'HIGH': '#f44336',
      'CRITICAL': '#d32f2f'
    };
    return colors[riskLevel] || '#757575';
  };

  const getPassRateColor = (passRate) => {
    if (passRate >= 95) return '#4caf50';
    if (passRate >= 85) return '#ff9800';
    return '#f44336';
  };

  const filteredResults = currentResults?.results.filter(result => {
    if (selectedFilter === 'all') return true;
    if (selectedFilter === 'passed') return result.passed;
    if (selectedFilter === 'failed') return !result.passed;
    return result.expected_category.toLowerCase() === selectedFilter.toLowerCase();
  });

  return (
    <div className="admin-test-runner">
      <header className="admin-header">
        <h1>🧪 Sys-Admin Test Runner</h1>
        <p>Classification Validation & Regression Testing</p>
      </header>

      <div className="test-controls">
        <div className="upload-section">
          <h2>Upload Test Cases</h2>
          
          <input
            type="text"
            placeholder="Test Run Name (optional)"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            className="test-name-input"
          />

          <div className="file-upload">
            <label htmlFor="csv-upload" className="upload-label">
              {csvFile ? csvFile.name : 'Choose CSV File'}
            </label>
            <input
              id="csv-upload"
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              className="file-input"
            />
          </div>

          {csvValidation && (
            <div className={`validation-result ${csvValidation.valid ? 'valid' : 'invalid'}`}>
              <h3>{csvValidation.valid ? '✅ Valid' : '❌ Invalid'}</h3>
              <p>{csvValidation.message}</p>
              <p>Total: {csvValidation.total_rows} | Valid: {csvValidation.valid_rows}</p>
              
              {csvValidation.errors?.length > 0 && (
                <div className="errors">
                  <h4>Errors:</h4>
                  <ul>
                    {csvValidation.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {csvValidation.preview?.length > 0 && (
                <div className="preview">
                  <h4>Preview (first 10 rows):</h4>
                  <table>
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Input Text</th>
                        <th>Expected</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvValidation.preview.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.channel}</td>
                          <td className="input-preview">{item.input_text.substring(0, 50)}...</td>
                          <td>{item.expected_category}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <button
            onClick={executeTestRun}
            disabled={loading || !csvValidation?.valid}
            className="run-button"
          >
            {loading ? 'Running Tests...' : 'Execute Test Run'}
          </button>
        </div>

        <div className="history-section">
          <h2>Recent Test Runs</h2>
          <div className="history-list">
            {testRuns.map((run) => (
              <div key={run.id} className="history-item">
                <div className="history-header">
                  <span className="run-name">{run.name}</span>
                  <span className="run-date">
                    {new Date(run.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="history-stats">
                  <span>Tests: {run.total_tests}</span>
                  <span className="pass-rate" style={{color: getPassRateColor(run.pass_rate)}}>
                    Pass Rate: {run.pass_rate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {currentResults && (
        <div className="results-section">
          <h2>Test Results</h2>

          <div className="summary-cards">
            <div className="summary-card">
              <h3>Total Tests</h3>
              <div className="stat-value">{currentResults.summary.total_tests}</div>
            </div>
            <div className="summary-card passed">
              <h3>Passed</h3>
              <div className="stat-value">{currentResults.summary.passed}</div>
            </div>
            <div className="summary-card failed">
              <h3>Failed</h3>
              <div className="stat-value">{currentResults.summary.failed}</div>
            </div>
            <div className="summary-card">
              <h3>Pass Rate</h3>
              <div 
                className="stat-value"
                style={{color: getPassRateColor(currentResults.summary.pass_rate)}}
              >
                {currentResults.summary.pass_rate}%
              </div>
            </div>
          </div>

          <div className="breakdowns">
            <div className="breakdown-card">
              <h3>By Category</h3>
              {Object.entries(currentResults.by_category).map(([category, stats]) => (
                <div key={category} className="breakdown-item">
                  <span>{category}</span>
                  <span>{stats.passed}/{stats.total}</span>
                  <span className="breakdown-rate">{stats.pass_rate}%</span>
                </div>
              ))}
            </div>

            <div className="breakdown-card">
              <h3>By Channel</h3>
              {Object.entries(currentResults.by_channel).map(([channel, stats]) => (
                <div key={channel} className="breakdown-item">
                  <span>{channel}</span>
                  <span>{stats.passed}/{stats.total}</span>
                  <span className="breakdown-rate">{stats.pass_rate}%</span>
                </div>
              ))}
            </div>
          </div>

          <div className="filter-controls">
            <button onClick={() => setSelectedFilter('all')} className={selectedFilter === 'all' ? 'active' : ''}>
              All
            </button>
            <button onClick={() => setSelectedFilter('passed')} className={selectedFilter === 'passed' ? 'active' : ''}>
              Passed Only
            </button>
            <button onClick={() => setSelectedFilter('failed')} className={selectedFilter === 'failed' ? 'active' : ''}>
              Failed Only
            </button>
          </div>

          <div className="results-table">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Channel</th>
                  <th>Input Text</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Confidence</th>
                  <th>Explanation</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults?.map((result, idx) => (
                  <tr key={idx} className={result.passed ? 'passed-row' : 'failed-row'}>
                    <td>
                      <span className="status-icon">
                        {result.passed ? '✅' : '❌'}
                      </span>
                    </td>
                    <td>{result.channel}</td>
                    <td className="text-cell">{result.input_text}</td>
                    <td>
                      <span className="category-badge">{result.expected_category}</span>
                      {result.expected_risk && (
                        <span className="risk-badge" style={{backgroundColor: getRiskColor(result.expected_risk)}}>
                          {result.expected_risk}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="category-badge">{result.actual_category}</span>
                      <span className="risk-badge" style={{backgroundColor: getRiskColor(result.actual_risk)}}>
                        {result.actual_risk}
                      </span>
                    </td>
                    <td>{(result.confidence * 100).toFixed(0)}%</td>
                    <td className="explanation-cell">{result.explanation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTestRunner;
